import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '../layouts';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../hooks/useApiClient';
import analyticsService from '../services/analytics';
import PromoCodeModal from '../components/PromoCodeModal';

// GraphQL mutation for updating email preferences
const updateEmailPreferencesMutation = `
  mutation UpdateEmailPreferences($input: EmailPreferencesInput!) {
    updateEmailPreferences(input: $input) {
      id
      emailPreferences {
        marketingConsent
        weeklyTipsSubscribed
      }
    }
  }
`;

// GraphQL query for getting email preferences directly from Brevo
const getEmailPreferencesFromBrevoQuery = `
  query GetUserEmailPreferencesFromBrevo {
    getUserEmailPreferencesFromBrevo {
      marketingConsent
      weeklyTipsSubscribed
      midweekTipsSubscribed
    }
  }
`;

// GraphQL mutation for changing password
const changePasswordMutation = `
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

// GraphQL mutation for updating profile
const updateProfileMutation = `
  mutation UpdateProfile($firstName: String!, $lastName: String!, $email: String!) {
    updateProfile(firstName: $firstName, lastName: $lastName, email: $email) {
      id
      firstName
      lastName
      email
    }
  }
`;

// GraphQL mutation for requesting phone update
const requestPhoneUpdateMutation = `
  mutation RequestPhoneUpdate($newPhone: String!, $reason: String!) {
    requestPhoneUpdate(newPhone: $newPhone, reason: $reason)
  }
`;

// GraphQL mutation for closing account
const closeAccountMutation = `
  mutation CloseAccount($password: String!, $reason: String) {
    closeAccount(password: $password, reason: $reason)
  }
`;

const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  
  // Use the API client hook
  const { client, isLoading: isClientLoading, error: clientError, getClient } = useApiClient();
  
  // Email preferences state
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [weeklyTipsSubscribed, setWeeklyTipsSubscribed] = useState(false);
  const [midweekTipsSubscribed, setMidweekTipsSubscribed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  
  // Collapsible sections state
  const [isUpdateProfileExpanded, setIsUpdateProfileExpanded] = useState(false);
  const [isChangePasswordExpanded, setIsChangePasswordExpanded] = useState(false);
  const [isPhoneUpdateExpanded, setIsPhoneUpdateExpanded] = useState(false);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Profile update state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);
  
  // Phone update request state
  const [newPhone, setNewPhone] = useState('');
  const [phoneReason, setPhoneReason] = useState('');
  const [isRequestingPhoneUpdate, setIsRequestingPhoneUpdate] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState(false);
  
  // Account closure state
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isClosingAccount, setIsClosingAccount] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  
  // Analytics page view tracking
  useEffect(() => {
    try {
      analyticsService.trackPageView('/profile', 'Profile Page');
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }, []);

  // Log client error if present
  useEffect(() => {
    if (clientError) {
      console.error('API client error:', clientError);
    }
  }, [clientError]);
  
  // Initialize profile fields when user data is available
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setEmail(user.email || '');
    }
  }, [user]);
  
  // Fetch email preferences directly from Brevo when component mounts
  useEffect(() => {
    // Create a flag to track if the component is mounted
    let isMounted = true;
    
    const fetchEmailPreferences = async () => {
      // Skip if already loading
      if (isLoadingPreferences) return;
      
      try {
        setIsLoadingPreferences(true);
        
        // Get API client
        let apiClient = client;
        if (!apiClient) {
          apiClient = await getClient();
        }
        
        // Query Brevo directly for email preferences
        console.log('Fetching email preferences directly from Brevo...');
        const response: any = await apiClient.graphql({
          query: getEmailPreferencesFromBrevoQuery
        });
        
        // Only update state if component is still mounted
        if (isMounted) {
          // Update state with preferences from Brevo
          if (response?.data?.getUserEmailPreferencesFromBrevo) {
            const prefs = response.data.getUserEmailPreferencesFromBrevo;
            console.log('Email preferences from Brevo:', prefs);
            setMarketingConsent(prefs.marketingConsent || false);
            setWeeklyTipsSubscribed(prefs.weeklyTipsSubscribed || false);
            setMidweekTipsSubscribed(prefs.midweekTipsSubscribed || false);
          }
        }
      } catch (error) {
        console.error('Error fetching email preferences from Brevo:', error);
        // Fall back to user data if Brevo query fails
        if (user?.emailPreferences && isMounted) {
          console.log('Falling back to user data for email preferences');
          setMarketingConsent(user.emailPreferences.marketingConsent);
          setWeeklyTipsSubscribed(user.emailPreferences.weeklyTipsSubscribed);
        }
      } finally {
        if (isMounted) {
          setIsLoadingPreferences(false);
        }
      }
    };
    
    // Only fetch if user is available
    if (user) {
      fetchEmailPreferences();
    }
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once on mount
  
  const handleLogout = async () => {
    try {
      // Track logout event
      try {
        analyticsService.trackEvent('user_logout', {
          metadata: {
            source: 'profile_page',
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
      
      await logout();
      // Redirect is handled by the AuthContext
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  const handleSaveEmailPreferences = async () => {
    try {
      setIsUpdating(true);
      setUpdateMessage('');
      
      let apiClient = client;
      
      // If client is not initialized, try to initialize it
      if (!apiClient) {
        try {
          console.log('Client not initialized, attempting to initialize...');
          apiClient = await getClient();
        } catch (clientError) {
          console.error('Error initializing API client:', clientError);
          setUpdateSuccess(false);
          setUpdateMessage('Failed to initialize API client. Please try again.');
          return;
        }
      }
      
      // Call the GraphQL mutation to update email preferences
      await apiClient.graphql({
        query: updateEmailPreferencesMutation,
        variables: {
          input: {
            marketingConsent,
            weeklyTipsSubscribed,
            midweekTipsSubscribed
          }
        }
      });
      
      setUpdateSuccess(true);
      setUpdateMessage('Email preferences updated successfully');
      
      // Track email preferences update
      try {
        analyticsService.trackEvent('email_preferences_updated', {
          metadata: {
            marketingConsent,
            weeklyTipsSubscribed,
            midweekTipsSubscribed,
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
      
      // Fetch the latest email preferences from Brevo
      console.log('Fetching latest email preferences after saving...');
      try {
        // Query Brevo directly for email preferences
        const response: any = await apiClient.graphql({
          query: getEmailPreferencesFromBrevoQuery
        });
        
        // Update state with preferences from Brevo
        if (response?.data?.getUserEmailPreferencesFromBrevo) {
          const prefs = response.data.getUserEmailPreferencesFromBrevo;
          console.log('Updated email preferences from Brevo:', prefs);
          setMarketingConsent(prefs.marketingConsent);
          setWeeklyTipsSubscribed(prefs.weeklyTipsSubscribed);
          setMidweekTipsSubscribed(prefs.midweekTipsSubscribed);
        }
      } catch (fetchError) {
        console.error('Error fetching updated email preferences:', fetchError);
        // Don't show an error message to the user, as the preferences were saved successfully
      }
      
      // Clear the message after 3 seconds
      setTimeout(() => {
        setUpdateMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error updating email preferences:', error);
      setUpdateSuccess(false);
      setUpdateMessage('Failed to update email preferences. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      // Validate passwords
      if (!currentPassword || !newPassword || !confirmPassword) {
        setPasswordSuccess(false);
        setPasswordMessage('Please fill in all password fields.');
        return;
      }

      if (newPassword !== confirmPassword) {
        setPasswordSuccess(false);
        setPasswordMessage('New passwords do not match.');
        return;
      }

      if (newPassword.length < 8) {
        setPasswordSuccess(false);
        setPasswordMessage('New password must be at least 8 characters long.');
        return;
      }

      setIsChangingPassword(true);
      setPasswordMessage('');

      let apiClient = client;
      if (!apiClient) {
        apiClient = await getClient();
      }

      // Call the GraphQL mutation to change password
      await apiClient.graphql({
        query: changePasswordMutation,
        variables: {
          currentPassword,
          newPassword
        }
      });

      setPasswordSuccess(true);
      setPasswordMessage('Password changed successfully');
      
      // Track password change
      try {
        analyticsService.trackEvent('password_changed', {
          metadata: {
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Clear message after 3 seconds
      setTimeout(() => {
        setPasswordMessage('');
      }, 3000);
    } catch (error: any) {
      console.error('Error changing password:', error);
      setPasswordSuccess(false);
      setPasswordMessage(error.message || 'Failed to change password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      // Validate fields
      if (!firstName || !lastName || !email) {
        setProfileSuccess(false);
        setProfileMessage('Please fill in all profile fields.');
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setProfileSuccess(false);
        setProfileMessage('Please enter a valid email address.');
        return;
      }

      setIsUpdatingProfile(true);
      setProfileMessage('');

      let apiClient = client;
      if (!apiClient) {
        apiClient = await getClient();
      }

      // Call the GraphQL mutation to update profile
      await apiClient.graphql({
        query: updateProfileMutation,
        variables: {
          firstName,
          lastName,
          email
        }
      });

      setProfileSuccess(true);
      setProfileMessage('Profile updated successfully');
      
      // Track profile update
      try {
        analyticsService.trackEvent('profile_updated', {
          metadata: {
            fieldsUpdated: ['firstName', 'lastName', 'email'],
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }

      // Clear message after 3 seconds
      setTimeout(() => {
        setProfileMessage('');
      }, 3000);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setProfileSuccess(false);
      setProfileMessage(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleRequestPhoneUpdate = async () => {
    try {
      // Validate fields
      if (!newPhone || !phoneReason) {
        setPhoneSuccess(false);
        setPhoneMessage('Please fill in both phone number and reason fields.');
        return;
      }

      // Basic phone validation
      const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
      if (!phoneRegex.test(newPhone)) {
        setPhoneSuccess(false);
        setPhoneMessage('Please enter a valid phone number.');
        return;
      }

      setIsRequestingPhoneUpdate(true);
      setPhoneMessage('');

      let apiClient = client;
      if (!apiClient) {
        apiClient = await getClient();
      }

      // Call the GraphQL mutation to request phone update
      await apiClient.graphql({
        query: requestPhoneUpdateMutation,
        variables: {
          newPhone,
          reason: phoneReason
        }
      });

      setPhoneSuccess(true);
      setPhoneMessage('Phone update request submitted successfully. Our support team will contact you within 2-3 business days.');
      
      // Track phone update request
      try {
        analyticsService.trackEvent('phone_update_requested', {
          metadata: {
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
      
      // Clear form
      setNewPhone('');
      setPhoneReason('');

      // Clear message after 5 seconds (longer for this message)
      setTimeout(() => {
        setPhoneMessage('');
      }, 5000);
    } catch (error: any) {
      console.error('Error requesting phone update:', error);
      setPhoneSuccess(false);
      setPhoneMessage(error.message || 'Failed to submit phone update request. Please try again.');
    } finally {
      setIsRequestingPhoneUpdate(false);
    }
  };

  const handleCloseAccount = async () => {
    try {
      // Validate password
      if (!deletePassword) {
        setDeleteMessage('Please enter your password to confirm account closure.');
        return;
      }

      setIsClosingAccount(true);
      setDeleteMessage('');

      let apiClient = client;
      if (!apiClient) {
        apiClient = await getClient();
      }

      // Call the GraphQL mutation to close account
      await apiClient.graphql({
        query: closeAccountMutation,
        variables: {
          password: deletePassword,
          reason: deleteReason || 'User requested account closure'
        }
      });

      // Account closed successfully - logout and redirect
      setDeleteMessage('Account closed successfully. You will be logged out shortly.');
      
      // Track account closure
      try {
        analyticsService.trackEvent('account_closed', {
          metadata: {
            reason: deleteReason || 'User requested account closure',
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        console.error('Analytics tracking error:', error);
      }
      
      setTimeout(async () => {
        await logout();
      }, 2000);
    } catch (error: any) {
      console.error('Error closing account:', error);
      setDeleteMessage(error.message || 'Failed to close account. Please try again.');
    } finally {
      setIsClosingAccount(false);
    }
  };
  
  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Your Profile</h1>
        
        {user ? (
          <div>
            <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
              <h2 className="text-lg font-semibold mb-2">Personal Information</h2>
              <div className="space-y-2">
                <p>
                  <span className="font-medium">Name:</span> {user.firstName} {user.lastName}
                </p>
                <p>
                  <span className="font-medium">Phone:</span> {user.phoneNumber}
                </p>
                <p>
                  <span className="font-medium">Email:</span> {user.email}
                </p>
              </div>
            </div>
            
            {/* Subscription Management Section - Moved up */}
            <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold">Membership Management</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    View and manage your current and past memberships, cancel active memberships
                  </p>
                </div>
                <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setPromoModalOpen(true)}
                    className="border border-racing-green text-racing-green py-2 px-4 rounded hover:bg-racing-green hover:text-white transition flex items-center justify-center w-full sm:w-auto text-center text-sm font-medium"
                  >
                    Redeem a Promo Code
                  </button>
                  <Link
                    to="/profile/subscriptions"
                    className="bg-racing-green text-white py-2 px-4 rounded hover:bg-racing-green-dark transition flex items-center justify-center w-full sm:w-auto text-center"
                  >
                    <span className="sm:hidden">Manage Memberships</span>
                    <span className="hidden sm:inline">Manage Memberships →</span>
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Email Preferences Section */}
            <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Email Preferences</h2>
                {isLoadingPreferences && (
                  <div className="flex items-center text-racing-green">
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">Loading preferences...</span>
                  </div>
                )}
              </div>
              
              {updateMessage && (
                <div className={`p-3 rounded mb-4 ${updateSuccess ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {updateSuccess && (
                    <svg 
                      className="w-5 h-5 inline-block mr-2 text-green-600" 
                      fill="currentColor" 
                      viewBox="0 0 20 20" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {updateMessage}
                </div>
              )}
              
              {isLoadingPreferences ? (
                <div className="py-4 text-center text-gray-500">
                  <p>Loading your email preferences from Brevo...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="weeklyTipsSubscribed"
                      checked={weeklyTipsSubscribed}
                      onChange={(e) => setWeeklyTipsSubscribed(e.target.checked)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <label htmlFor="weeklyTipsSubscribed" className="font-medium cursor-pointer">
                        Weekly Racing Tips
                      </label>
                      <p className="text-sm text-gray-600">
                        Receive our weekly email with top racing tips and predictions
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="midweekTipsSubscribed"
                      checked={midweekTipsSubscribed}
                      onChange={(e) => setMidweekTipsSubscribed(e.target.checked)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <label htmlFor="midweekTipsSubscribed" className="font-medium cursor-pointer">
                        Midweek Tip Email
                      </label>
                      <p className="text-sm text-gray-600">
                        Receive midweek racing tips and analysis
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="marketingConsent"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <label htmlFor="marketingConsent" className="font-medium cursor-pointer">
                        Marketing Emails
                      </label>
                      <p className="text-sm text-gray-600">
                        Receive occasional emails about special offers, promotions, and updates
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveEmailPreferences}
                    disabled={isUpdating || !client || isClientLoading}
                    className={`mt-2 bg-racing-green text-white py-2 px-4 rounded hover:bg-racing-green-dark transition flex items-center ${
                      isUpdating || !client || isClientLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isUpdating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : isClientLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Initializing...
                      </>
                    ) : (
                      'Save Preferences'
                    )}
                  </button>
                </div>
              )}
            </div>
            
            {/* Update Profile Section - Collapsible */}
            <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
              <button
                onClick={() => setIsUpdateProfileExpanded(!isUpdateProfileExpanded)}
                className="w-full flex justify-between items-center text-left"
              >
                <h2 className="text-lg font-semibold">Update Profile</h2>
                <svg
                  className={`w-5 h-5 transform transition-transform ${isUpdateProfileExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isUpdateProfileExpanded && (
                <div className="mt-4">
                  {profileMessage && (
                    <div className={`p-3 rounded mb-4 ${profileSuccess ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                      {profileSuccess && (
                        <svg className="w-5 h-5 inline-block mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {profileMessage}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-racing-green"
                        placeholder="Enter your first name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-racing-green"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-racing-green"
                      placeholder="Enter your email address"
                    />
                  </div>
                  
                  <button
                    onClick={handleUpdateProfile}
                    disabled={isUpdatingProfile || !client || isClientLoading}
                    className={`mt-4 bg-racing-green text-white py-2 px-4 rounded hover:bg-racing-green-dark transition flex items-center ${
                      isUpdatingProfile || !client || isClientLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isUpdatingProfile ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      'Update Profile'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Change Password Section - Collapsible */}
            <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
              <button
                onClick={() => setIsChangePasswordExpanded(!isChangePasswordExpanded)}
                className="w-full flex justify-between items-center text-left"
              >
                <h2 className="text-lg font-semibold">Change Password</h2>
                <svg
                  className={`w-5 h-5 transform transition-transform ${isChangePasswordExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isChangePasswordExpanded && (
                <div className="mt-4">
                  {passwordMessage && (
                    <div className={`p-3 rounded mb-4 ${passwordSuccess ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                      {passwordSuccess && (
                        <svg className="w-5 h-5 inline-block mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {passwordMessage}
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-racing-green"
                        placeholder="Enter your current password"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-racing-green"
                        placeholder="Enter your new password"
                      />
                      <p className="text-sm text-gray-600 mt-1">Password must be at least 8 characters long</p>
                    </div>
                    
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-racing-green"
                        placeholder="Confirm your new password"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={handleChangePassword}
                    disabled={isChangingPassword || !client || isClientLoading}
                    className={`mt-4 bg-racing-green text-white py-2 px-4 rounded hover:bg-racing-green-dark transition flex items-center ${
                      isChangingPassword || !client || isClientLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isChangingPassword ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Phone Update Request Section - Collapsible */}
            <div className="mb-6 p-4 bg-racing-gray-light rounded-lg">
              <button
                onClick={() => setIsPhoneUpdateExpanded(!isPhoneUpdateExpanded)}
                className="w-full flex justify-between items-center text-left"
              >
                <h2 className="text-lg font-semibold">Request Phone Number Change</h2>
                <svg
                  className={`w-5 h-5 transform transition-transform ${isPhoneUpdateExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isPhoneUpdateExpanded && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Phone number changes require manual processing by our support team. Please provide your new phone number and reason for the change.
                  </p>
                  
                  {phoneMessage && (
                    <div className={`p-3 rounded mb-4 ${phoneSuccess ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                      {phoneSuccess && (
                        <svg className="w-5 h-5 inline-block mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {phoneMessage}
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="newPhone" className="block text-sm font-medium text-gray-700 mb-1">
                        New Phone Number
                      </label>
                      <input
                        type="tel"
                        id="newPhone"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-racing-green"
                        placeholder="Enter your new phone number"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phoneReason" className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Change
                      </label>
                      <textarea
                        id="phoneReason"
                        value={phoneReason}
                        onChange={(e) => setPhoneReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-racing-green"
                        placeholder="Please explain why you need to change your phone number"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={handleRequestPhoneUpdate}
                    disabled={isRequestingPhoneUpdate || !client || isClientLoading}
                    className={`mt-4 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition flex items-center ${
                      isRequestingPhoneUpdate || !client || isClientLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isRequestingPhoneUpdate ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      'Submit Request'
                    )}
                  </button>
                </div>
              )}
            </div>

            
            <div className="flex flex-col items-center space-y-3">
              <button
                onClick={handleLogout}
                className="bg-racing-red text-white py-2 px-4 rounded hover:bg-racing-red-dark transition"
              >
                Logout
              </button>
              
              {/* Small Close Account Link */}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-sm text-gray-500 hover:text-red-600 transition underline"
              >
                Close Account
              </button>
            </div>

            {/* Account Closure Modal */}
            {showDeleteConfirm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-4">Close Account</h3>
                  <p className="text-sm text-red-700 mb-4">
                    ⚠️ <strong>Warning:</strong> This action will permanently delete your account and all associated data. This cannot be undone.
                  </p>
                  
                  {deleteMessage && (
                    <div className="p-3 rounded mb-4 bg-red-100 text-red-800 border border-red-300">
                      {deleteMessage}
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="deletePasswordModal" className="block text-sm font-medium text-red-700 mb-1">
                        Enter your password to confirm
                      </label>
                      <input
                        type="password"
                        id="deletePasswordModal"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Enter your password"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="deleteReasonModal" className="block text-sm font-medium text-red-700 mb-1">
                        Reason for closing account (optional)
                      </label>
                      <textarea
                        id="deleteReasonModal"
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Please let us know why you're closing your account"
                      />
                    </div>
                    
                    <div className="flex space-x-3 pt-4">
                      <button
                        onClick={handleCloseAccount}
                        disabled={isClosingAccount || !deletePassword}
                        className={`bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition flex items-center ${
                          isClosingAccount || !deletePassword ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                      >
                        {isClosingAccount ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Closing Account...
                          </>
                        ) : (
                          'Confirm - Close Account'
                        )}
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeletePassword('');
                          setDeleteReason('');
                          setDeleteMessage('');
                        }}
                        className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-gray-500">
            <p>Loading profile information...</p>
          </div>
        )}
      </div>
      <PromoCodeModal isOpen={promoModalOpen} onClose={() => setPromoModalOpen(false)} />
    </MainLayout>
  );
};

export default ProfilePage;
